// apps/admin-ui/src/pages/LoginPage.tsx
// Admin UI Login Page with OTP authentication
// ALL RIGHTS RESERVED

import { useState, useEffect } from 'react';
import { canAccessApp } from '../utils/rbac';
import {
  setStoredEmployeeId,
  setStoredFingerprint,
  setStoredEmployeeData,
  clearAuthData,
} from '../utils/auth';
import * as authApi from '../api/auth';
import type { Employee } from '../utils/auth';

type LoginStep = 'email' | 'otp' | 'dev-select';

interface LoginPageProps {
  onLogin: () => void;
}

export function LoginPage({ onLogin }: LoginPageProps) {
  // Form state
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Dev mode state
  const [devEmployees, setDevEmployees] = useState<Employee[]>([]);
  const [selectedDevId, setSelectedDevId] = useState<string>('');

  // Check if dev mode
  const isDev = import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEV_LOGIN === 'true';

  // Fetch employees for dev mode
  useEffect(() => {
    if (isDev) {
      authApi.getEmployees()
        .then(employees => {
          // Filter to only employees that can access admin-ui
          const adminEmployees = employees.filter(e => canAccessApp(e.roles, 'admin-ui'));
          // Sort by email for consistent ordering and easier identification
          adminEmployees.sort((a, b) => a.email.localeCompare(b.email));
          setDevEmployees(adminEmployees);
        })
        .catch(err => console.error('Failed to fetch employees:', err));
    }
  }, [isDev]);

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError(null);

    try {
      await authApi.employeeLogin(email);
      setStep('otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send login code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim() || otp.length !== 6) return;

    setLoading(true);
    setError(null);

    try {
      const result = await authApi.employeeVerify(email, otp);
      
      // Check if employee can access admin-ui
      if (!canAccessApp(result.employee.roles, 'admin-ui')) {
        setError('You do not have access to Admin UI. Required roles: compliance, contract_auditor, treasury, council, security_admin, accounting, or admin.');
        return;
      }
      
      // Store auth data
      setStoredEmployeeId(result.employee.id);
      if (!result.devMode) {
        setStoredFingerprint(result.certificate.fingerprint);
      }
      setStoredEmployeeData(result.employee);
      
      onLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handleDevLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDevId) return;

    const employee = devEmployees.find(e => e.id === selectedDevId);
    if (!employee) {
      setError('Employee not found');
      return;
    }

    // Check access
    if (!canAccessApp(employee.roles, 'admin-ui')) {
      setError('Selected employee does not have access to Admin UI');
      return;
    }

    clearAuthData();
    setStoredEmployeeId(employee.id);
    setStoredEmployeeData(employee);
    
    onLogin();
  };

  const handleBack = () => {
    setStep('email');
    setOtp('');
    setError(null);
  };

  // Format employee for dropdown - use email as primary identifier to avoid duplicates
  const formatEmployeeOption = (emp: Employee): string => {
    const roleDisplay = emp.roles.length > 1 
      ? `${emp.roles[0]} +${emp.roles.length - 1}`
      : emp.roles[0] || 'unknown';
    return `${emp.email} (${roleDisplay})`;
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">
            <svg viewBox="0 0 64 64" fill="none" width="64" height="64">
              <circle cx="32" cy="32" r="28" stroke="#00d4aa" strokeWidth="2" />
              <path d="M32 14 L46 22.5 L46 41.5 L32 50 L18 41.5 L18 22.5 Z" stroke="#00d4aa" strokeWidth="2" />
              <circle cx="32" cy="32" r="8" fill="#00d4aa" />
            </svg>
          </div>
          <h1>CXXR Admin</h1>
          <p>Internal Administration Dashboard</p>
        </div>

        {error && (
          <div className="login-error">
            {error}
          </div>
        )}

        {step === 'email' && (
          <form onSubmit={handleRequestOtp} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={loading}
                autoFocus
              />
            </div>
            <button type="submit" className="btn-primary" disabled={loading || !email.trim()}>
              {loading ? 'Sending...' : 'Send Login Code'}
            </button>
            
            {isDev && devEmployees.length > 0 && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setStep('dev-select')}
              >
                Dev Login (Skip OTP)
              </button>
            )}
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={handleVerifyOtp} className="login-form">
            <p className="otp-message">
              Enter the 6-digit code sent to <strong>{email}</strong>
            </p>
            <div className="form-group">
              <label htmlFor="otp">Verification Code</label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                disabled={loading}
                autoFocus
              />
              {isDev && (
                <p className="dev-hint">Dev hint: Use code 123456</p>
              )}
            </div>
            <button type="submit" className="btn-primary" disabled={loading || otp.length !== 6}>
              {loading ? 'Verifying...' : 'Verify & Login'}
            </button>
            <button type="button" className="btn-link" onClick={handleBack}>
              ← Back to email
            </button>
          </form>
        )}

        {step === 'dev-select' && (
          <form onSubmit={handleDevLogin} className="login-form">
            <p className="dev-message">
              Select an employee to login as (dev mode only)
            </p>
            <div className="form-group">
              <label htmlFor="dev-employee">Employee</label>
              <select
                id="dev-employee"
                value={selectedDevId}
                onChange={(e) => setSelectedDevId(e.target.value)}
              >
                <option value="">Select employee...</option>
                {devEmployees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {formatEmployeeOption(emp)}
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" className="btn-primary" disabled={!selectedDevId}>
              Login as Selected Employee
            </button>
            <button type="button" className="btn-link" onClick={handleBack}>
              ← Back to email login
            </button>
          </form>
        )}

        <div className="login-footer">
          <p>Access restricted to authorized personnel only.</p>
        </div>
      </div>

      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          padding: 1rem;
        }
        
        .login-container {
          background: #1e1e2e;
          border-radius: 12px;
          padding: 2rem;
          width: 100%;
          max-width: 480px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }
        
        .login-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        
        .login-logo {
          display: flex;
          justify-content: center;
          margin-bottom: 1rem;
        }
        
        .login-header h1 {
          color: #fff;
          margin: 0 0 0.5rem;
          font-size: 1.75rem;
        }
        
        .login-header p {
          color: #888;
          margin: 0;
          font-size: 0.9rem;
        }
        
        .login-error {
          background: rgba(220, 38, 38, 0.1);
          border: 1px solid rgba(220, 38, 38, 0.3);
          color: #f87171;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          font-size: 0.9rem;
        }
        
        .login-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .form-group label {
          color: #ccc;
          font-size: 0.875rem;
        }
        
        .form-group input,
        .form-group select {
          padding: 0.75rem 1rem;
          border: 1px solid #333;
          border-radius: 8px;
          background: #2a2a3e;
          color: #fff;
          font-size: 0.9rem;
        }
        
        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #6366f1;
        }
        
        .otp-message,
        .dev-message {
          color: #ccc;
          text-align: center;
          margin: 0;
        }
        
        .dev-hint {
          color: #f59e0b;
          font-size: 0.8rem;
          margin: 0.25rem 0 0;
        }
        
        .btn-primary {
          padding: 0.75rem 1.5rem;
          background: #6366f1;
          color: #fff;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          cursor: pointer;
          transition: background 0.2s;
        }
        
        .btn-primary:hover:not(:disabled) {
          background: #4f46e5;
        }
        
        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .btn-secondary {
          padding: 0.75rem 1.5rem;
          background: transparent;
          color: #6366f1;
          border: 1px solid #6366f1;
          border-radius: 8px;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .btn-secondary:hover {
          background: rgba(99, 102, 241, 0.1);
        }
        
        .btn-link {
          background: none;
          border: none;
          color: #888;
          cursor: pointer;
          font-size: 0.9rem;
          padding: 0.5rem;
        }
        
        .btn-link:hover {
          color: #fff;
        }
        
        .login-footer {
          margin-top: 2rem;
          text-align: center;
        }
        
        .login-footer p {
          color: #666;
          font-size: 0.8rem;
          margin: 0;
        }
      `}</style>
    </div>
  );
}

